import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Message } from 'primeng/api';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppSettings } from '../app.settings';
import { LoaderService } from './../components/loader/loader.service';
import { StorageService } from './storage.service';

@Injectable()
export class RestApiService {
  shoeErrorMessage = new Subject<any>();
  message: Message[] = [];
  private readonly offlineErrorMessage = 'Internet is not connected. Please check your network and try again.';
  private readonly unreachableErrorMessage = 'Unable to connect to the server. Please check your network and try again.';

  constructor(
    private http: HttpClient,
    private router: Router,
    private storageService: StorageService,
    private loaderService: LoaderService
  ) {
  }

  private prependApiUrl(url: string): string {
    return AppSettings.BASE_URL + '/' + AppSettings.TENANT + url;
  }

  private isBrowserOnline(): boolean {
    return typeof navigator === 'undefined' ? true : navigator.onLine;
  }

  private isNetworkError(error: HttpErrorResponse | any): boolean {
    if (!this.isBrowserOnline()) {
      return true;
    }

    const status = error?.status ?? error?.statusCode;
    return status === 0;
  }

  private getNetworkErrorMessage(): string {
    return this.isBrowserOnline() ? this.unreachableErrorMessage : this.offlineErrorMessage;
  }

  private createNetworkError() {
    const message = this.getNetworkErrorMessage();
    return {
      general: [{ message }]
    };
  }

  private notifyNetworkError(): void {
    this.shoeErrorMessage.next(this.getNetworkErrorMessage());
  }

  private executeRequest<T>(request$: Observable<T>, touchLoader = true): Observable<T> {
    if (!this.isBrowserOnline()) {
      if (touchLoader) {
        this.hideLoader();
      }
      this.notifyNetworkError();
      return throwError(() => this.createNetworkError());
    }

    return request$.pipe(catchError((error) => this.handleError(error, touchLoader)));
  }

  get(url: string, loader?: string): Observable<{}> {
    this.showLoader(loader);
    return this.executeRequest(
      this.http.get(this.prependApiUrl(url), { headers: this.getHeaders() })
    ).pipe(
      map((r: Response) => {
        this.hideLoader();
        return r;
      })
    );
  }

  /**
   * GET that neither shows nor hides the shared page loader. LoaderService has no request counter, so a background read
   * that hid it would end the spinner of other requests still in flight.
   */
  getInBackground(url: string): Observable<{}> {
    return this.executeRequest(this.http.get(this.prependApiUrl(url), { headers: this.getHeaders() }), false);
  }

  getText(url: string, loader?: string): Observable<string> {
    this.showLoader(loader);
    return this.executeRequest(
      this.http.get(this.prependApiUrl(url), { headers: this.getHeaders(), responseType: 'text' })
    ).pipe(
      map((r: string) => {
        this.hideLoader();
        return r;
      })
    );
  }

  post(url: string, body: any, loader?: string, options = { headers: this.getHeaders() }): Observable<{}> {
    this.showLoader(loader);
    return this.executeRequest(
      this.http.post(this.prependApiUrl(url), body, options)
    ).pipe(
      map((r: Response) => {
        this.hideLoader();
        return r;
      })
    );
  }

  put(url: string, body?: any, loader?: string, options = { headers: this.getHeaders() }): Observable<{}> {
    this.showLoader(loader);
    return this.executeRequest(
      this.http.put(this.prependApiUrl(url), body, options)
    ).pipe(
      map((r: Response) => {
        this.hideLoader();
        return r;
      })
    );
  }

  delete(url: string, loader?: string, options = { headers: this.getHeaders() }): Observable<{}> {
    this.showLoader(loader);
    return this.executeRequest(
      this.http.delete(this.prependApiUrl(url), options)
    ).pipe(
      map((r: Response) => {
        this.hideLoader();
        return r;
      })
    );
  }

  patch(url: string, body: any, loader?: string, options = { headers: this.getHeaders() }): Observable<{}> {
    this.showLoader(loader);
    return this.executeRequest(
      this.http.patch(this.prependApiUrl(url), body, options)
    ).pipe(
      map((r: Response) => {
        this.hideLoader();
        return r;
      })
    );
  }

  head(url: string, loader?: string, options = { headers: this.getHeaders() }): Observable<{}> {
    this.showLoader(loader);
    return this.executeRequest(
      this.http.head(this.prependApiUrl(url), options)
    ).pipe(
      map((r: Response) => {
        this.hideLoader();
        return r;
      })
    );
  }

  options(url: string, loader?: string, options = { headers: this.getHeaders() }): Observable<{}> {
    this.showLoader(loader);
    return this.executeRequest(
      this.http.options(this.prependApiUrl(url), options)
    ).pipe(
      map((r: Response) => {
        this.hideLoader();
        return r;
      })
    );
  }

  excel(url: string, fileName: string, loader?: string) {
    this.showLoader(loader);
    return this.http.get(this.prependApiUrl(url), { headers: this.getHeaders(), responseType: 'blob', observe: 'response' }).subscribe
      (data => {
        let name = data.headers.get('content-disposition');
        if (name === undefined || name === null || name === '') {
          name = fileName;
        } else {
          name = name.substring(name.indexOf('='));
          name = name.replace('=', '');
        }
        this.downloadFile(data.body, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', name);
      }),
      error => this.handleError(error);
  }

  excelPost(url: string, body: any, fileName: string, loader?: string) {
    this.showLoader(loader);
    return this.http.post(this.prependApiUrl(url), body, {
      headers: this.getHeaders(),
      responseType: 'blob',
      observe: 'response'
    }).subscribe(
      data => {
        let name = data.headers.get('content-disposition');
        if (name === undefined || name === null || name === '') {
          name = fileName;
        } else {
          name = name.substring(name.indexOf('='));
          name = name.replace('=', '');
        }
        this.downloadFile(data.body, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', name);
      }),
      error => this.handleError(error);
  }

  pdf(url: string, fileName: string, loader?: string,
    options = {
      headers: this.getHeaders(),
      responseType: 'blob'
    }) {
    this.showLoader(loader);
    return this.http.get(this.prependApiUrl(url), { headers: this.getHeaders(), responseType: 'blob', observe: 'response' }).subscribe
      (data => {
        let name = data.headers.get('content-disposition');
        if (name === undefined || name === null || name === '') {
          name = fileName;
        } else {
          name = name.substring(name.indexOf('='));
          name = name.replace('=', '');
        }
        this.downloadFile(data.body, 'application/pdf', name);
      }),
      error => this.handleError(error);
  }

  pdfPost(url: string, body: any, fileName: string, loader?: string) {
    this.showLoader(loader);
    return this.http.post(this.prependApiUrl(url), body, {
      headers: this.getHeaders(),
      responseType: 'blob',
      observe: 'response'
    }).subscribe(
      data => {
        let name = data.headers.get('content-disposition');
        if (name === undefined || name === null || name === '') {
          name = fileName;
        } else {
          name = name.substring(name.indexOf('='));
          name = name.replace('=', '');
        }
        this.downloadFile(data.body, 'application/pdf', name);
      }),
      error => this.handleError(error);
  }

  image(url: string, fileName: string, loader?: string) {
    this.showLoader(loader);
    return this.http.get(url, { headers: this.getHeaders(), responseType: 'blob' }).subscribe(data =>
      this.downloadFile(data, this.getContentType(fileName), fileName)),
      error => this.handleError(error);
  }

  private getHeaders(): HttpHeaders {
    let headers = new HttpHeaders({
      'Accept-Language': AppSettings.HEADER_ACCEPT_LANGUAGE,
      'Content-Type': AppSettings.HEADER_CONTENT_TYPE,
      'Accept': AppSettings.HEADER_CONTENT_TYPE,
      'x-app-type': AppSettings.APP_TYPE
    });

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (AppSettings.HEADER_AUTHORIZATION_VALUE !== null) {
      headers = new HttpHeaders({
        'Accept-Language': AppSettings.HEADER_ACCEPT_LANGUAGE,
        'Content-Type': AppSettings.HEADER_CONTENT_TYPE,
        'Accept': AppSettings.HEADER_CONTENT_TYPE,
        'Authorization': 'Bearer ' + this.storageService.getItemFromSessionStorage(AppSettings.TOKEN_KEY),
        'Timezone': timeZone,
        'x-app-type': AppSettings.APP_TYPE
      });
    }
    return headers;
  }

  private handleError(error: HttpErrorResponse | any, touchLoader = true) {
    if (touchLoader) {
      this.hideLoader();
    }

    if (this.isNetworkError(error)) {
      this.notifyNetworkError();
      return throwError(() => this.createNetworkError());
    }

    if (error.status === 400) {
      return throwError(() => error.error);
    } else if (error.status === 500 || error.status === 403) {
      return throwError(() => error.error);
    } else if (error.status === 401) {
      this.storageService.removeAll();
      this.storageService.clearSessionStorage();
      AppSettings.HEADER_AUTHORIZATION_VALUE = null;
      if (!AppSettings.isPublicRoute(this.router.url)) {
        this.router.navigate(['/signin']);
      }
      return throwError(() => error.error || error);
    } else if (error.status === 502) {
      this.shoeErrorMessage.next('Server is not available for this moment. Please try again.');
      return throwError(() => error.error || error);
    }
    return throwError(() => error.error || error);
  }

  downloadFile(data: any, contentType: string, fileName: string) {
    const blob = new Blob([data], { type: contentType });
    const link = document.createElement('a');
    link.setAttribute('type', 'hidden');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    this.hideLoader();
    setTimeout(() => {
      link.remove()
    }, 1000);
  }

  private getContentType(fileName: string) {
    const extension = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
    switch (extension) {
      case 'jpeg':
        return 'image/jpeg';
      case 'jpg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'gif':
        return 'image/gif';
      case 'bmp':
        return 'image/x-ms-bmp';
      case 'pdf':
        return 'application/pdf';
      case 'xls':
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }
    return '';
  }

  private showLoader(loader?: string): void {
    if (loader !== undefined && loader !== null && 'none' !== loader.toLowerCase()) {
      this.loaderService.show(loader);
    }
  }

  private hideLoader(): void {
    this.loaderService.hide();
  }

  // special case wit body
  deleteUser(url: string, loader?: string, body?): Observable<{}> {
    this.showLoader(loader);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const options = {
      headers: new HttpHeaders({
        'Accept-Language': AppSettings.HEADER_ACCEPT_LANGUAGE,
        'Content-Type': AppSettings.HEADER_CONTENT_TYPE,
        'Accept': AppSettings.HEADER_CONTENT_TYPE,
        'Authorization': 'Bearer ' + this.storageService.getItemFromSessionStorage(AppSettings.TOKEN_KEY),
        'Timezone': timeZone,
        'x-app-type': AppSettings.APP_TYPE
      }),
      body: body
    }
    return this.executeRequest(
      this.http.delete(this.prependApiUrl(url), options)
    ).pipe(
      map((r: Response) => {
        this.hideLoader();
        return r;
      })
    );
  }
}
